import {Spin, Typography} from "antd"
import StripeElementsProvider from "providers/StripeElementsProvider"
import {useMemo, useState} from "react"
import {useTranslation} from "react-i18next"
import {PaypalRecurringSubscriptionForm} from "sections/StudentAccount/Student/parts/StudentLedgerTab/parts/RecurringPayments/PaypalRecurringSubscriptionForm"
import {
    getRecurringPaymentFromPaymentPlan,
    RecurringPaymentFromPaymentPlan
} from "sections/StudentAccount/Student/parts/StudentLedgerTab/parts/RecurringPayments/RecurringPaymentPopup"
import StripeRecurringPaymentForm from "sections/StudentAccount/Student/parts/StudentLedgerTab/parts/RecurringPayments/StripeRecurringPaymentForm"
import {PaymentPlan} from "types/student-account/payment-plan"
import {PreparePaypalRecurringSubscriptionApiResponse} from "types/student-account/student-payment"
import {usePaymentProviderConfigs} from "hooks/usePaymentProviderConfigs"

type Props = {
    profileId: number
    paymentPlan: Partial<PaymentPlan>
    onSuccess: () => void
    isPreparing?: boolean
    paypalPrepare?: PreparePaypalRecurringSubscriptionApiResponse | null
}

export const RecurringPaymentCheckout = ({
    profileId,
    paymentPlan,
    onSuccess,
    isPreparing = false,
    paypalPrepare = null
}: Props) => {
    const {t} = useTranslation(["payment", "common"])
    const [loadKey] = useState(() => Math.random())
    const {isStripeEnabled, isPaypalEnabled, paypalProviderConfig} = usePaymentProviderConfigs(profileId)

    const recurring: RecurringPaymentFromPaymentPlan = useMemo(
        () => getRecurringPaymentFromPaymentPlan(paymentPlan),
        [paymentPlan]
    )

    const amountInCents = Math.round((recurring.amount || 0) * 100)
    const checkoutWithStripe = isStripeEnabled
    const checkoutWithPaypal = isPaypalEnabled && !isStripeEnabled

    if (isPreparing) {
        return <Spin />
    }

    return (
        <div>
            {checkoutWithStripe && (
                <StripeElementsProvider profileId={profileId} loadKey={loadKey}>
                    <StripeRecurringPaymentForm
                        profileId={profileId}
                        onSuccess={onSuccess}
                        amount={recurring.amount}
                        currency="usd"
                        interval={recurring.interval}
                        intervalCount={recurring.intervalCount}
                        cancelAt={null}
                        paymentPlanItems={recurring.paymentPlanItems}
                        submitButtonTitle="PAY"
                        preferNewCard
                    />
                </StripeElementsProvider>
            )}

            {checkoutWithPaypal && (
                <>
                    {paypalProviderConfig?.clientId ? (
                        <PaypalRecurringSubscriptionForm
                            key={loadKey}
                            profileId={profileId}
                            clientId={paypalProviderConfig.clientId}
                            amountInCents={amountInCents}
                            currency="usd"
                            interval={recurring.interval}
                            intervalCount={recurring.intervalCount}
                            cancelAt={null}
                            paymentPlanItems={recurring.paymentPlanItems}
                            paymentPlanId={paymentPlan.paymentPlanId}
                            existingPrepare={
                                paypalPrepare?.paypalPlanId && paypalPrepare?.recurringPaymentId
                                    ? {
                                          recurringPaymentId: paypalPrepare.recurringPaymentId,
                                          paypalPlanId: paypalPrepare.paypalPlanId
                                      }
                                    : undefined
                            }
                            onSuccess={onSuccess}
                        />
                    ) : (
                        <Typography.Paragraph type="danger">
                            {t("msg.recurringPayment.paypalMissingClientConfig")}
                        </Typography.Paragraph>
                    )}
                </>
            )}
        </div>
    )
}
